import { useState } from 'react'
import {
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
} from '@mui/material'
import {
  InstallMobile as InstallMobileIcon,
  IosShare as IosShareIcon,
  AddBoxOutlined as AddBoxIcon,
} from '@mui/icons-material'
import usePwaInstall from '@hooks/usePwaInstall'

/**
 * Top-bar "Install app" affordance.
 *
 * - Chromium (a deferred prompt is available): an icon button that fires the
 *   native install prompt on click.
 * - iOS Safari (no prompt event): the same icon opens a short "Add to Home
 *   Screen" instructions dialog.
 * - Already installed / running standalone / not installable: renders nothing.
 */
const InstallButton = ({ sx }) => {
  const { canInstall, isIOS, isStandalone, installed, promptInstall } = usePwaInstall()
  const [iosOpen, setIosOpen] = useState(false)

  if (isStandalone || installed) return null

  // Chromium: native prompt available.
  if (canInstall) {
    return (
      <Tooltip title="Install app">
        <IconButton
          onClick={promptInstall}
          aria-label="Install app"
          sx={{ color: 'text.secondary', ...sx }}
        >
          <InstallMobileIcon />
        </IconButton>
      </Tooltip>
    )
  }

  // iOS Safari: show manual add-to-home-screen instructions.
  if (isIOS) {
    return (
      <>
        <Tooltip title="Install app">
          <IconButton
            onClick={() => setIosOpen(true)}
            aria-label="Install app"
            sx={{ color: 'text.secondary', ...sx }}
          >
            <InstallMobileIcon />
          </IconButton>
        </Tooltip>
        <Dialog
          open={iosOpen}
          onClose={() => setIosOpen(false)}
          aria-labelledby="pwa-ios-install-title"
          maxWidth="xs"
          fullWidth
        >
          <DialogTitle id="pwa-ios-install-title">Install DuoNep</DialogTitle>
          <DialogContent>
            <Typography variant="body2" color="text.secondary" mb={1}>
              Add DuoNep to your Home Screen to launch it like an app:
            </Typography>
            <List dense>
              <ListItem disableGutters>
                <ListItemIcon sx={{ minWidth: 36 }}>
                  <IosShareIcon fontSize="small" color="primary" />
                </ListItemIcon>
                <ListItemText primary="Tap the Share button in Safari's toolbar." />
              </ListItem>
              <ListItem disableGutters>
                <ListItemIcon sx={{ minWidth: 36 }}>
                  <AddBoxIcon fontSize="small" color="primary" />
                </ListItemIcon>
                <ListItemText primary="Choose 'Add to Home Screen'." />
              </ListItem>
              <ListItem disableGutters>
                <ListItemIcon sx={{ minWidth: 36 }}>
                  <Box
                    aria-hidden="true"
                    sx={{ width: 20, textAlign: 'center', fontWeight: 700, color: 'primary.main' }}
                  >
                    3
                  </Box>
                </ListItemIcon>
                <ListItemText primary="Tap 'Add' to finish." />
              </ListItem>
            </List>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setIosOpen(false)}>Got it</Button>
          </DialogActions>
        </Dialog>
      </>
    )
  }

  // Not installable (e.g. desktop Firefox, or already installed elsewhere).
  return null
}

export default InstallButton
